import { createContext, useContext, useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import type { Session } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'

interface AuthValue {
  session: Session | null
  ready: boolean
  /** True when this session arrived on an invitation or reset link and the
   *  person has not chosen a password yet. */
  mustSetPassword: boolean
  clearPasswordPrompt: () => void
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthValue | null>(null)

/**
 * Reads the fragment Supabase puts on an invitation or reset link.
 *
 * Read ONCE, before anything can clear it, and read from the hash rather than
 * the query string because that is where Supabase puts it and because a hash
 * is never sent to a server or written to an access log. The token in it is a
 * credential.
 */
function linkType(): string | null {
  try {
    const hash = window.location.hash.startsWith('#')
      ? window.location.hash.slice(1)
      : window.location.hash
    return new URLSearchParams(hash).get('type')
  } catch {
    return null
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [ready, setReady] = useState(false)
  const [mustSetPassword, setMustSetPassword] = useState(
    () => linkType() === 'invite' || linkType() === 'recovery',
  )

  useEffect(() => {
    let live = true

    supabase.auth.getSession().then(({ data }) => {
      if (!live) return
      setSession(data.session)
      setReady(true)
    })

    const { data: sub } = supabase.auth.onAuthStateChange((event, next) => {
      setSession(next)
      // Supabase raises this for both a reset link and an invitation. Either
      // way the person is signed in but has no password of their own yet, and
      // dropping them into the console would leave an account nobody can get
      // back into once the link expires.
      if (event === 'PASSWORD_RECOVERY') setMustSetPassword(true)
      if (event === 'SIGNED_OUT') setMustSetPassword(false)
    })

    return () => {
      live = false
      sub.subscription.unsubscribe()
    }
  }, [])

  const value: AuthValue = {
    session,
    ready,
    mustSetPassword,
    clearPasswordPrompt: () => {
      setMustSetPassword(false)
      // Drop the token from the address bar so a shared reception terminal
      // does not keep a working credential in its history.
      try {
        window.history.replaceState(null, '', window.location.pathname)
      } catch {
        // Cosmetic. Not worth failing over.
      }
    },
    signOut: async () => {
      await supabase.auth.signOut()
    },
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext)
  if (value === null) throw new Error('useAuth must be used inside AuthProvider')
  return value
}
